import { useSyncExternalStore } from 'react';

/**
 * Which layout the screen gets (docs/REDESIGN-2026-10-20.md §1):
 *  - phone: the mockup flows (portrait, under 700 px wide);
 *  - tablet: a side sheet over one shared map, with a top bar; `compact` (a landscape phone) drops the top bar;
 *  - desktop: top bar, left panel, large live map.
 * The numbers are applied as CSS variables on <html> so CSS and JS always agree.
 */
export type LayoutKind = 'phone' | 'tablet' | 'desktop';
export type Layout = {
  kind: LayoutKind;
  /** tablet or desktop: map + side panel */
  wide: boolean;
  /** a landscape phone: no top bar, the tab bar stays in the panel */
  compact: boolean;
  /** side panel width (px) */
  panelW: number;
  /** space around the panel (px) */
  gap: number;
  /** top bar height (px), 0 without one */
  topBar: number;
  /** where the free part of the map starts, from the left of the map (px) */
  mapLeft: number;
  /** desktop: the panel is docked to the left edge, full height (the Uber-web shape); tablets float a side sheet */
  docked: boolean;
};

export function layoutFor(w: number, h: number): Layout {
  const landscapePhone = w >= 600 && w > h && h < 560;
  const wide = w >= 700 || landscapePhone;
  if (!wide) return { kind: 'phone', wide: false, compact: false, panelW: Math.min(w, 480), gap: 0, topBar: 0, mapLeft: 0, docked: false };
  const compact = h < 560;
  const kind: LayoutKind = w >= 1180 && !compact ? 'desktop' : 'tablet';
  const panelW =
    kind === 'desktop' ? (w >= 1800 ? 468 : w >= 1400 ? 440 : 420) : compact ? Math.round(Math.max(320, Math.min(372, w * 0.46))) : w < 900 ? 352 : 380;
  const gap = kind === 'desktop' ? 20 : compact ? 10 : 16;
  const topBar = compact ? 0 : kind === 'desktop' ? 68 : 60;
  const docked = kind === 'desktop';
  return { kind, wide: true, compact, panelW, gap, topBar, mapLeft: docked ? panelW : gap + panelW, docked };
}

const read = (): Layout =>
  typeof window === 'undefined' ? layoutFor(390, 844) : layoutFor(window.innerWidth, window.innerHeight);

let current = read();
const listeners = new Set<() => void>();

/** Put the layout on <html> (data attributes + CSS variables). */
export function applyLayout(l: Layout = current) {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.dataset.layout = l.kind;
  if (l.wide) el.dataset.wide = '';
  else delete el.dataset.wide;
  if (l.compact) el.dataset.compact = '';
  else delete el.dataset.compact;
  if (l.docked) el.dataset.docked = '';
  else delete el.dataset.docked;
  el.style.setProperty('--panel-w', `${l.panelW}px`);
  el.style.setProperty('--gap', `${l.gap}px`);
  el.style.setProperty('--topbar', `${l.topBar}px`);
  el.style.setProperty('--map-l', `${l.mapLeft}px`);
}

if (typeof window !== 'undefined') {
  applyLayout();
  window.addEventListener('resize', () => {
    const next = read();
    if (JSON.stringify(next) === JSON.stringify(current)) return;
    current = next;
    applyLayout(next);
    listeners.forEach((f) => f());
  });
}

const subscribe = (f: () => void) => {
  listeners.add(f);
  return () => listeners.delete(f);
};

/** The current layout; re-renders when the window crosses into another one (or the panel width changes). */
export const useLayout = (): Layout => useSyncExternalStore(subscribe, () => current, () => current);

/**
 * How much of the map's bottom the phone's sheet covers (the map container stops there). On wide screens the map
 * fills the stage and the panel sits on its left instead, so nothing is cut from the bottom.
 */
export const mapCut = (l: Layout, sheetH: number, overlap: number) => (l.wide ? 0 : Math.max(0, sheetH - overlap));
